// hooks
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";

// components
import Title from "@components/Title";
import SimplePageHeader from "@components/SimplePageHeader";

// context
import { toast } from "react-toastify";
import ImageSelector from "@components/CreatePage/ImageSelector";
import api from "@utils/api";
import Konva from "konva";
import { Layer, Stage } from "react-konva";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShapes, faTrash } from "@fortawesome/free-solid-svg-icons";
import GradientBtn from "@ui/GradientBtn";
import { ClipLoader } from "react-spinners";

const CreatePage = () => {
  const stageRef = useRef(null);
  const [imageLayers, setImageLayers] = useState([]);
  const [selectedImageLayer, setSelectedImageLayer] = useState(null);
  const [selectedTransformLayer, setSelectedTransformLayer] = useState(null);
  const [selectedBorderLayer, setSelectedBorderLayer] = useState(null);

  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const params = useParams();

  const token = localStorage.getItem("token");

  const convertToPNG = async () => {
    try {
      const stage = stageRef.current.getStage();
      const dataURL = stage.toDataURL();
      // console.log(dataURL);

      return dataURL;
    } catch (error) {
      console.error("Error converting to PNG:", error);
      throw error;
    }
  };

  const extractImageCoordinates = (image) => {
    // console.log(image);
    return {
      image: image.id(),
      x: image.x(),
      y: image.y(),
      width: image.width(),
      height: image.height(),
    };
  };

  // onSubmit function will execute when create button get clicked
  const onSubmit = () => {
    setIsLoading(true);
    convertToPNG()
      .then((dataURL) => {
        // const file = dataURLtoFile(
        //   dataURL,
        //   `comic_${params.id}_page_number_${params.pageNumber}_page_image.jpg`
        // );

        const base64Data = dataURL.split(",")[1]; // Extract base64 data

        // You can perform any further processing with the image
        const headers = {
          Authorization: `Bearer ${token}`,
          // "Content-Type": "multipart/form-data",
        };

        const imagesCoordinates = imageLayers.map(extractImageCoordinates);
        const data = {
          comic_book_id: params.id,
          page_number: params.pageNumber,
          images: imagesCoordinates,
          page_image: base64Data, // Include base64 image data directly
        };
        // console.log(data);
        // const formData = new FormData();
        // formData.append("comic_book_id", params.id);
        // formData.append("page_number", params.pageNumber);
        // formData.append("page_image", file);
        // imageLayers.forEach((image) => {
        // const coordinates = extractImageCoordinates(image);
        // console.log(coordinates);

        // formData.append("images", [image.id()]);
        // });

        api
          .post("/create-comic-page", data, { headers })
          .then((res) => {
            setIsLoading(false);
            if (res?.data?.status == "200") {
              localStorage.setItem("showSuccessToast", true);
              localStorage.setItem(
                "createComicPageResponse",
                "Comic Page Created Successfully!"
              );
            }
            navigate(`/creation/${params?.id}`);
            console.log(res);
          })
          .catch((error) => {
            console.error("Error:", error);
            setIsLoading(false);
          });
      })
      .catch((error) => {
        setIsLoading(false);
      });
  };

  const addImage = (image, ratio) => {
    // console.log(ratio);
    const stage = stageRef.current;
    const layer = new Konva.Layer();
    stage.add(layer);

    const img = new window.Image();
    //img.src = `http://127.0.0.1:8000${image.image}`;
    img.src = image.image;
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const konvaImage = new Konva.Image({
        image: img,
        width: ratio * 200,
        height: 200,
        x: 10,
        y: 10,
        id: `${image.id}`,
        // name: `image-${image.id}`,
        draggable: true,
      });

      // Add a black border around the image
      const border = new Konva.Rect({
        x: 10,
        y: 10,
        width: konvaImage.width(),
        height: konvaImage.height(),
        stroke: "black",
        strokeWidth: 10,
        // listening: false, // So that the border doesn't interfere with image drag
      });

      layer.add(border);
      layer.add(konvaImage);

      konvaImage.on("dragmove", () => {
        // Update the position of the border to match the image
        border.position({
          x: konvaImage.x(),
          y: konvaImage.y(),
        });
        // Redraw the layer
        layer.batchDraw();
      });

      konvaImage.on("click", () => {
        const tr = new Konva.Transformer({
          nodes: [konvaImage],
          keepRatio: true,
          flipEnabled: false,
          boundBoxFunc: (oldBox, newBox) => {
            const box = getClientRect(newBox);
            const isOut =
              box.x < 0 ||
              box.y < 0 ||
              box.x + box.width > stage.width() ||
              box.y + box.height > stage.height();

            if (isOut) {
              return oldBox;
            }
            return newBox;
          },
        });
        layer.add(tr);

        tr.on("transform", () => {
          const scaleX = konvaImage.scaleX();
          const scaleY = konvaImage.scaleY();

          // Update the position and size of the border to match the transformed image
          border.position({
            x: konvaImage.x(),
            y: konvaImage.y(),
          });
          border.width(konvaImage.width() * scaleX);
          border.height(konvaImage.height() * scaleY);

          // Redraw the layer
          layer.batchDraw();

          konvaImage.setAttrs({
            scaleX: 1,
            scaleY: 1,
            width: konvaImage.width() * konvaImage.scaleX(),
            height: konvaImage.height() * konvaImage.scaleY(),
          });
          applyCrop(konvaImage, konvaImage.getAttr("lastCropUsed"));
          setSelectedImageLayer(konvaImage);
          setSelectedTransformLayer(tr);
          setSelectedBorderLayer(border);
        });

        setSelectedTransformLayer(tr);
        setSelectedImageLayer(konvaImage);
        setSelectedBorderLayer(border);
      });
      setSelectedBorderLayer(border);
      setSelectedImageLayer(konvaImage);

      applyCrop(konvaImage, "center-middle");

      setImageLayers((prevImageLayers) => [...prevImageLayers, konvaImage]);
    };
  };

  // Add an event listener to the stage to handle clicks outside of the image
  if (stageRef.current) {
    stageRef.current.on("click", (e) => {
      // Check if the clicked element is not the image
      if (e.target === stageRef.current) {
        // Remove the transformer if it exists
        if (selectedTransformLayer) {
          selectedTransformLayer.destroy();
          setSelectedTransformLayer(null);
        }
      }
    });
  }

  const getCrop = (image, size, clipPosition = "center-middle", canvasSize) => {
    const width = size.width;
    const height = size.height;
    const aspectRatio = width / height;

    // Adjust newWidth and newHeight based on canvas size
    let newWidth = Math.min(canvasSize.width, image.width);
    let newHeight = newWidth / aspectRatio;

    if (newHeight > canvasSize.height) {
      newHeight = canvasSize.height;
      newWidth = newHeight * aspectRatio;
    }

    let x = 0;
    let y = 0;
    if (clipPosition === "left-top") {
      x = 0;
      y = 0;
    } else if (clipPosition === "left-middle") {
      x = 0;
      y = (image.height - newHeight) / 2;
    } else if (clipPosition === "left-bottom") {
      x = 0;
      y = image.height - newHeight;
    } else if (clipPosition === "center-top") {
      x = (image.width - newWidth) / 2;
      y = 0;
    } else if (clipPosition === "center-middle") {
      x = (image.width - newWidth) / 2;
      y = (image.height - newHeight) / 2;
    } else if (clipPosition === "center-bottom") {
      x = (image.width - newWidth) / 2;
      y = image.height - newHeight;
    } else if (clipPosition === "right-top") {
      x = image.width - newWidth;
      y = 0;
    } else if (clipPosition === "right-middle") {
      x = image.width - newWidth;
      y = (image.height - newHeight) / 2;
    } else if (clipPosition === "right-bottom") {
      x = image.width - newWidth;
      y = image.height - newHeight;
    } else if (clipPosition === "scale") {
      x = 0;
      y = 0;
      newWidth = width;
      newHeight = height;
    } else {
      console.error(
        new Error("Unknown clip position property - " + clipPosition)
      );
    }

    return {
      cropX: x,
      cropY: y,
      cropWidth: newWidth,
      cropHeight: newHeight,
    };
  };

  const applyBounding = (image, crop, canvasWidth, canvasHeight) => {
    const { cropX, cropY, cropWidth, cropHeight } = crop;

    // Adjust crop values to ensure the image stays within the canvas
    let x = cropX;
    let y = cropY;
    let width = cropWidth;
    let height = cropHeight;

    // Apply bounding logic here
    // if (x < 0) {
    //   x = 0;
    // }
    // if (y < 0) {
    //   y = 0;
    // }
    // if (x + width > canvasWidth) {
    //   width = canvasWidth - x;
    // }
    // if (y + height > canvasHeight) {
    //   height = canvasHeight - y;
    // }

    return {
      cropX: x,
      cropY: y,
      cropWidth: width,
      cropHeight: height,
    };
  };

  const getCorner = (pivotX, pivotY, diffX, diffY, angle) => {
    const distance = Math.sqrt(diffX * diffX + diffY * diffY);
    angle += Math.atan2(diffY, diffX);

    const x = pivotX + distance * Math.cos(angle);
    const y = pivotY + distance * Math.sin(angle);

    return { x: x, y: y };
  };

  const getClientRect = (rotatedBox) => {
    const { x, y, width, height } = rotatedBox;
    const rad = rotatedBox.rotation;

    const p1 = getCorner(x, y, 0, 0, rad);
    const p2 = getCorner(x, y, width, 0, rad);
    const p3 = getCorner(x, y, width, height, rad);
    const p4 = getCorner(x, y, 0, height, rad);

    const minX = Math.min(p1.x, p2.x, p3.x, p4.x);
    const minY = Math.min(p1.y, p2.y, p3.y, p4.y);
    const maxX = Math.max(p1.x, p2.x, p3.x, p4.x);
    const maxY = Math.max(p1.y, p2.y, p3.y, p4.y);

    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
    };
  };

  const applyCrop = (image, clipPosition) => {
    const canvasWidth = window.innerWidth;
    const canvasHeight = window.innerHeight;

    const crop = getCrop(
      image.image(),
      { width: image.width(), height: image.height() },
      clipPosition,
      { width: canvasWidth, height: canvasHeight } // Pass canvas size to getCrop
    );

    // Apply bounding logic
    const boundedCrop = applyBounding(image, crop, canvasWidth, canvasHeight);

    image.setAttrs(boundedCrop);
    image.setAttr("lastCropUsed", clipPosition);
  };

  console.log(imageLayers);

  const handleDelete = () => {
    if (selectedImageLayer) {
      const selectedImageId = selectedImageLayer.attrs.id; // Assuming id() method returns the _id of the selected image

      // Filter out the image from imageLayers based on _id
      const updatedImageLayers = imageLayers.filter((image) => {
        return image.attrs.id !== selectedImageId;
      });

      // Update the array of imageLayers
      setImageLayers(updatedImageLayers);

      // console.log(selectedImageLayer.layer, "///");
      selectedImageLayer.destroy();
      selectedBorderLayer.destroy();
      setSelectedTransformLayer(null);
      setSelectedImageLayer(null);
      setSelectedBorderLayer(null);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("token")) {
      navigate("/login");
      toast.info("Login to get access to Image Generation");
    }
  }, [navigate]);

  return (
    <>
      <Title title="Create Comic Page" />
      <SimplePageHeader title="Create Comic Page" />
      <main>
        <ImageSelector
          addImage={addImage}
          handleDelete={handleDelete}
          imagesLayers={imageLayers}
        />
        <div className="d-flex flex-column g-20">
          {isLoading === false ? (
            <GradientBtn tag="button" type="submit" onClick={onSubmit}>
              Save
            </GradientBtn>
          ) : (
            <GradientBtn tag="button" type="submit" disabled>
              <ClipLoader color="#ffffff" size={30} />
            </GradientBtn>
          )}
        </div>

        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            marginTop: "20px",
            marginDown: "20px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {/* <button
              style={{
                margin: "10px",
              }}
            >
              <FontAwesomeIcon icon={faShapes} size="lg" color="#FCB808" />
            </button> */}
            <button
              style={{
                margin: "10px",
              }}
              onClick={handleDelete}
            >
              <FontAwesomeIcon icon={faTrash} size="lg" color="#FCB808" />
            </button>
          </div>
          <div
            style={{
              backgroundColor: "white",
              height: "900px",
              width: "670px",
            }}
          >
            <Stage
              width={window.width <= 425 ? 300 : 670}
              height={900}
              ref={stageRef}
            >
              <Layer />
            </Stage>
          </div>
        </div>
      </main>
    </>
  );
};

export default CreatePage;
